import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dialog, FormLabel, Group, Input, Typography, useModalPortal } from '@screentone/core';

import { useAlert } from 'contexts/alert/useAlert';
import { ArtifactType, usePublishArtifactSettingMutation } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';
import { ALPHANUMERIC_DASH_UNDERSCORE } from 'utils/regex';

interface CreateLayoutModalProps {
  onDismiss: () => void;
}

export const CreateLayoutModal = ({ onDismiss }: CreateLayoutModalProps) => {
  const { renderNode } = useModalPortal();
  const currentProperty = useConvertedProperty();
  const navigate = useNavigate();
  const { alertError } = useAlert();
  const { mutateAsync, isLoading } = usePublishArtifactSettingMutation();

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [idError, setIdError] = useState('');

  const idTrimmed = id.trim();
  const nameTrimmed = name.trim();
  const canSubmit = !!idTrimmed && !!nameTrimmed && !isLoading;

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = e.target.value.replace(/\s+/g, '-').replace(/[^a-z0-9_-]/gi, '');
    setId(sanitized);
    if (idError) setIdError('');
  };

  const handleSubmit = async () => {
    if (!canSubmit || !currentProperty) return;

    if (!ALPHANUMERIC_DASH_UNDERSCORE.test(idTrimmed)) {
      setIdError('ID may only contain letters, numbers, hyphens, and underscores.');
      return;
    }

    try {
      await mutateAsync({
        publicationKey: currentProperty,
        artifactId: idTrimmed,
        artifactType: ArtifactType.Layout,
        body: {
          id: idTrimmed,
          name: nameTrimmed
        }
      });
      navigate(`/${currentProperty}/site-config/layouts/${idTrimmed}`);
    } catch (err) {
      alertError(`Failed to create layout: ${(err as Error).message}`);
    }
  };

  return (
    <Dialog status="open" onDismiss={onDismiss} focusTrap renderNode={renderNode}>
      <Dialog.Title>New layout</Dialog.Title>
      <Dialog.Content>
        <Group direction="column" gap="md">
          <Group direction="column" gap="xs" fullWidth>
            <FormLabel label="Layout ID" labelPosition="top" fullWidth>
              <Input
                value={id}
                onChange={handleIdChange}
                placeholder="e.g. home"
                error={!!idError}
                disabled={isLoading}
              />
            </FormLabel>
            {idError && (
              <Typography variant="note" color="lava">
                {idError}
              </Typography>
            )}
            <Typography variant="note" color="asphalt">
              Letters, numbers, hyphens, and underscores only. Used in the URL and must be unique for this publication.
            </Typography>
          </Group>
          <FormLabel label="Name" labelPosition="top" fullWidth>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder="e.g. Home page"
              disabled={isLoading}
            />
          </FormLabel>
        </Group>
      </Dialog.Content>
      <Dialog.Actions>
        <Button secondary onClick={onDismiss} disabled={isLoading}>
          Cancel
        </Button>
        <Button primary onClick={() => void handleSubmit()} disabled={!canSubmit}>
          {isLoading ? 'Creating…' : 'Create'}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
